/* eslint-disable react/no-multi-comp */
import React, { useEffect, useMemo, useState } from 'react';

import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { withSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import { OfferInclusionsSelect } from '~/components/Common/Forms/fields';
import ReactHookFormAdapter from '~/components/Common/Forms/widgets/ReactHookFormAdapter';

import { prepareScheduledDiscountList } from '~/utils/scheduledDiscountsHelper';
import { ingestSchedules, prepareSchedulesList } from '~/utils/schedulesHelpers';

import { BONUS_INCLUSIONS_MAX_TO_NIGHTS } from '../../../consts/ratePlans';
import OffersService from '../../../services/OffersService';
import ReservationService from '../../../services/ReservationService';
import { getCancellationPolicyLabel } from '../../../utils/cancellationPolicy';
import nullsToEmptyStrings from '../../../utils/nullsToEmptyStrings';
import { reportError } from '../../../utils/reportError';
import { DeleteButton } from '../DeleteButton';
import SetRegionModal from '../Modals/SetRegionModal';

import ScheduledDiscount from './fields/ScheduledDiscount';
import Schedules from './fields/Schedules';
import { buttonMessages, buttonStates } from './states/submitButton';
import MarkdownEditor from './widgets/MarkdownEditor';

const mapRatePlanFormData = ({
  _links,
  id_salesforce_external,
  group,
  created_at,
  updated_at,
  id,
  bonus_inclusions,
  cancellation_policy_detail,
  ...form
}) => {
  const mappedRatePlan = {
    ...form,
    bonus_inclusions: (bonus_inclusions || []).map(({ id, ...bonusInclusion }) => {
      const mappedBonusInclusion = {
        ...bonusInclusion,
        content_override: bonusInclusion.content_override || [],
      };
      return mappedBonusInclusion;
    }),
    inclusions_override: form.inclusions_override || [],
  };
  if (!id) {
    mappedRatePlan.id_salesforce_external = id_salesforce_external;
  }
  mappedRatePlan.schedules_list = ingestSchedules(form?.schedules_list);
  const visibleDiscounts = ['internal', 'extranet']; // `external` source is still out
  mappedRatePlan.discounts = (form?.discounts ?? []).filter((discount) => visibleDiscounts.includes(discount.source));
  mappedRatePlan.stay_pay?.sort((a, b) => a.stay_nights - b.stay_nights);
  return nullsToEmptyStrings(mappedRatePlan);
};

const getRateTypeWidget = (rateType) => {
  function RateTypeWidget(props) {
    return (
      <TextField
        value={props.value}
        required={props.required}
        label={props.label}
        type={props.schema.type}
        onChange={(event) => props.onChange(event.target.value)}
        aria-readonly={props.registry.formContext.rateTypeValue !== rateType}
        inputProps={{
          readOnly: props.registry.formContext.rateTypeValue !== rateType,
        }}
      />
    );
  }
  return RateTypeWidget;
};

const setToMaxWidget = (props) => {
  const largestToNightsBonusInclusions = props.registry.formContext.bonusInclusions?.reduce(
    (max, x, index) => (x.to_nights > max.bonusInclusions.to_nights ? { index, bonusInclusions: x } : max),
    { index: -1, bonusInclusions: { to_nights: 0 } },
  );
  const currentIndex = Number(props.id.match(/root_bonus_inclusions_([0-9]+)_set_to_max/)[1]);

  const isMaxToNights =
    props.registry.formContext.bonusInclusions &&
    props.registry.formContext.bonusInclusions[currentIndex]?.to_nights === BONUS_INCLUSIONS_MAX_TO_NIGHTS;

  const isShorterThanMaxToNightInclusions =
    largestToNightsBonusInclusions.index !== -1 &&
    largestToNightsBonusInclusions.index !== currentIndex &&
    props.registry.formContext.bonusInclusions[currentIndex]?.from_nights <
      largestToNightsBonusInclusions.bonusInclusions.to_nights;

  const hasFromNightLargerThanLargestToNight =
    largestToNightsBonusInclusions.index !== -1 &&
    largestToNightsBonusInclusions.index === currentIndex &&
    props.registry.formContext.bonusInclusions.some(
      (inclusions) => inclusions.from_nights > props.registry.formContext.bonusInclusions[currentIndex]?.to_nights,
    );
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={isMaxToNights}
          onChange={(event) => props.onChange(event.target.checked)}
          disabled={
            isShorterThanMaxToNightInclusions ||
            !props.registry.formContext.bonusInclusions[currentIndex]?.from_nights ||
            hasFromNightLargerThanLargestToNight
          }
        />
      }
      label="Set to max"
    />
  );
};

const fromNightsWidget = (props) => (
  <TextField
    value={props.value}
    required={props.required}
    type={props.schema.type}
    onChange={(event) => props.onChange(event.target.value)}
  />
);

const toNightsWidget = (props) => {
  const currentIndex = Number(props.id.match(/root_bonus_inclusions_([0-9]+)_to_nights/)[1]);

  const isMaxToNights =
    props.registry.formContext.bonusInclusions &&
    props.registry.formContext.bonusInclusions[currentIndex]?.to_nights === BONUS_INCLUSIONS_MAX_TO_NIGHTS;

  if (isMaxToNights) {
    return (
      <TextField
        value={props.registry.formContext.bonusInclusions[currentIndex].from_nights + '+'}
        type="text"
        disabled
      />
    );
  }
  return (
    <TextField
      value={props.value}
      required={props.required}
      type={props.schema.type}
      onChange={(event) => props.onChange(event.target.value)}
    />
  );
};

const GrossRateTypeWidget = getRateTypeWidget('gross');
const NetRateTypeWidget = getRateTypeWidget('net');

const widgets = {
  markup: NetRateTypeWidget,
  commission: GrossRateTypeWidget,
  discount: GrossRateTypeWidget,
  setToMax: setToMaxWidget,
  toNights: toNightsWidget,
  fromNights: fromNightsWidget,
};

const DEFAULT_VALUE = 'Default';

const commissionSchema = {
  'ui:widget': 'commission',
  'ui:help': 'Gross - discount - commission = net',
};

const discountSchema = {
  'ui:widget': 'discount',
  'ui:help': 'Gross - discount = sale price',
};

const markupSchema = {
  'ui:widget': 'markup',
  'ui:help': 'Net + markup = gross',
};

const hiddenSchema = {
  'ui:widget': 'hidden',
};

const defaultUiSchema = {
  should_instant_purchase: { 'ui:widget': 'hidden' },
  deposit_percent: { 'ui:widget': 'hidden' },
  id_salesforce_external: { 'ui:widget': 'hidden' },
  default_plan: { 'ui:widget': 'hidden' },
  product_type: {
    'ui:help': 'The type of offer this rate plan will be used for',
  },
  rate_plan_code: { 'ui:emptyValue': null },
  rate_type: { 'ui:widget': 'radio' },
  markup: markupSchema,
  commission: commissionSchema,
  discount: discountSchema,
  discounts: {
    'ui:widget': 'hidden',
    'ui:disabled': true,
  },
  inclusions: { 'ui:widget': MarkdownEditor },

  // overwrites inclusions if filled. This will eventually replace inclusions completely
  inclusions_override: {
    'ui:widget': ReactHookFormAdapter,
    'ui:options': { ReactHookFormComponent: OfferInclusionsSelect },
  },
  bonus_inclusions: {
    items: {
      set_to_max: {
        'ui:widget': 'setToMax',
      },
      from_nights: {
        'ui:widget': 'fromNights',
      },
      to_nights: {
        'ui:widget': 'toNights',
      },
      content: {
        'ui:widget': MarkdownEditor,
      },
      content_override: {
        'ui:widget': ReactHookFormAdapter,
        'ui:options': { ReactHookFormComponent: OfferInclusionsSelect },
      },
    },
  },
  custom_vcc_payment_currency: { 'ui:widget': 'select' },
  currency: { 'ui:widget': 'select' },
  schedules: {
    'ui:widget': 'hidden',
    'ui:disabled': true,
  },
  schedules_list: {
    'ui:widget': 'hidden',
    'ui:disabled': true,
  },
  regions: {
    'ui:classNames': 'hidden-form-field',
    'ui:disabled': true,
  },
  bundle_and_save_discount_only: {
    'ui:title': 'Show on Single & Bundle Offer',
  },
  bundle_discount_percent: {
    'ui:widget': 'hidden',
  },
  is_stay_pay_enabled: {
    'ui:widget': 'hidden',
    'ui:disabled': true,
  },
  stay_pay: {
    'ui:widget': 'hidden',
    'ui:disabled': true,
  },
  'ui:order': [
    'name',
    'product_type',
    'rate_type',
    'markup',
    'commission',
    'discount',
    'type_of_pay',
    'deposit_percent',
    'b2b_special_payment_terms',
    'allow_free_nights',
    'ignore_dynamic_cancellation_policy',
    'cancellation_policy',
    'non_refundable_cancellation_policy_codes',
    'rate_plan_code',
    'inclusions',
    'inclusions_override',
    'bonus_inclusions',
    'custom_vcc_payment_currency',
    'currency',
    'is_packaged_rate',
    'packaged_rate_id',
    'status',
    'is_base_rate',
    'regions',
    '*',
  ],
};

function RatePlanForm({
  vendorId,
  schema: initialSchema,
  ratePlan: initialRatePlan,
  ratePlanId,
  packagedRatePlans,
  enqueueSnackbar,
}) {
  const history = useHistory();

  const [uiSchema, setUiSchema] = useState({});
  const [deleteState, setDeleteState] = useState('delete');
  const [ratePlan, setRatePlan] = useState(mapRatePlanFormData(initialRatePlan));
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // Create a memoized copy of the schema to modify - this ensures it only runs once
  const schema = useMemo(() => {
    const schemaCopy = JSON.parse(JSON.stringify(initialSchema));

    // temp override for `type_of_pay` until it's fixed on backend
    schemaCopy.properties.type_of_pay = {
      type: ['string', 'null'],
      title: 'Instant Purchase or Deposit',
      enum: ['', 'instant_purchase', 'deposit'],
      default: '',
    };

    schemaCopy.properties.b2b_special_payment_terms = {
      ...schemaCopy.properties.b2b_special_payment_terms,
      type: ['string', 'null'],
      enum: ['', ...schemaCopy.properties.b2b_special_payment_terms.enum],
      default: '',
    };

    schemaCopy.properties.bonus_inclusions.items.properties.setToMax = {
      title: 'Set to max duration',
      type: 'boolean',
    };

    schemaCopy.properties.cancellation_policy = {
      ...schemaCopy.properties.cancellation_policy,
      type: ['string', 'null'],
      enum: ['', ...schemaCopy.properties.cancellation_policy.enum],
      default: '',
    };

    schemaCopy.properties.currency = {
      ...schemaCopy.properties.currency,
      type: ['string', 'null'],
      enum: ['', ...schemaCopy.properties.currency.enum],
      default: '',
    };

    schemaCopy.properties.custom_vcc_payment_currency = {
      ...schemaCopy.properties.custom_vcc_payment_currency,
      type: ['string', 'null'],
      enum: ['', ...schemaCopy.properties.custom_vcc_payment_currency.enum],
      default: '',
    };

    schemaCopy.properties.source = {
      ...schemaCopy.properties.source,
      type: ['string', 'null'],
      enum: ['', ...schemaCopy.properties.source.enum],
      default: '',
    };

    schemaCopy.properties.bonus_inclusions.items.properties = {
      from_nights: schemaCopy.properties.bonus_inclusions.items.properties.from_nights,
      to_nights: schemaCopy.properties.bonus_inclusions.items.properties.to_nights,
      set_to_max: { title: 'Set to max duration', type: 'boolean' },
      content: schemaCopy.properties.bonus_inclusions.items.properties.content,
      content_override: schemaCopy.properties.bonus_inclusions.items.properties.content_override,
    };

    schemaCopy.properties.packaged_rate_id = {
      ...schemaCopy.properties.packaged_rate_id,
      default: '',
      enum: ['', ...packagedRatePlans.map((plan) => plan.id)],
    };

    // We are managing schedules_list as an object internally, converted to array on submit - uuid keyed
    schemaCopy.properties.schedules_list = {
      type: 'object',
      title: 'Schedules',
    };

    if (schemaCopy.properties.vcc_buffer) {
      schemaCopy.properties.vcc_buffer = {
        ...schemaCopy.properties.vcc_buffer,
        type: ['string', 'number', 'null'],
        enum: ['', ...schemaCopy.properties.vcc_buffer.enum],
        default: '',
      };
    }

    return schemaCopy;
  }, [initialSchema, packagedRatePlans]);

  const enumNamesUiSchema = useMemo(() => {
    const cancellationPolicyEnums = initialSchema?.properties?.cancellation_policy?.enum || [];
    const currencyEnums = initialSchema?.properties?.currency?.enum || [];
    const vccCurrencyEnums = initialSchema?.properties?.custom_vcc_payment_currency?.enum || [];

    return {
      type_of_pay: {
        'ui:emptyValue': '',
        'ui:enumNames': ['', 'Instant Purchase', 'Deposit'],
      },
      cancellation_policy: {
        'ui:emptyValue': '',
        'ui:enumNames': ['', ...cancellationPolicyEnums.map((value) => getCancellationPolicyLabel(value))],
      },
      currency: {
        'ui:emptyValue': '',
        'ui:enumNames': ['', ...currencyEnums],
      },
      custom_vcc_payment_currency: {
        'ui:emptyValue': '',
        'ui:enumNames': ['', ...vccCurrencyEnums],
      },
      packaged_rate_id: {
        'ui:emptyValue': '',
        'ui:enumNames': ['', ...packagedRatePlans.map((plan) => plan.name)],
      },
    };
  }, [initialSchema, packagedRatePlans]);

  useEffect(() => {
    let newUiSchema = { ...defaultUiSchema, ...enumNamesUiSchema };
    const { rate_type } = initialRatePlan;
    newUiSchema = {
      ...newUiSchema,
      commission: rate_type === 'net' ? hiddenSchema : commissionSchema,
      discount: rate_type === 'net' ? hiddenSchema : discountSchema,
      markup: rate_type === 'gross' ? hiddenSchema : markupSchema,
    };

    if (initialRatePlan.is_packaged_rate) {
      newUiSchema.packaged_rate_id = {
        'ui:widget': 'hidden',
      };
    }

    if (initialRatePlan.default_plan) {
      newUiSchema.status = {
        'ui:disabled': true,
        'ui:help': "You can't change default rate plan status.",
      };
    }

    if (initialRatePlan.type_of_pay === 'deposit') {
      newUiSchema.deposit_percent = {};
    }

    if (initialRatePlan.bundle_and_save_discount_only) {
      newUiSchema.bundle_discount_percent = {};
    }

    newUiSchema.inclusions_override = {
      ...newUiSchema.inclusions_override,
    };

    setUiSchema(newUiSchema);
  }, [initialRatePlan, enumNamesUiSchema]);

  const findUsage = async () => {
    const { properties, offers } = await fetchData();

    const relatedRoomRates = properties.reduce((acc, property) => {
      property?.room_types?.forEach((roomType) => {
        roomType.room_rates.forEach((roomRate) => {
          if (roomRate?.rate_plan.id === ratePlanId) {
            acc.add(roomRate.id);
          }
        });
      });
      return acc;
    }, new Set());

    const rateOffers = offers.filter((offer) => {
      return offer.packages.some(
        (p) =>
          relatedRoomRates.has(p.fk_room_rate_id) ||
          p.package_options?.some((o) => relatedRoomRates.has(o.fk_room_rate_id)),
      );
    });

    return rateOffers.length;
  };

  const fetchData = async () => {
    const [properties, offers] = await Promise.all([
      ReservationService.getProperties(vendorId),
      OffersService.getOffers({
        queryString: vendorId,
      }),
    ]).catch(function (e) {
      reportError(e);
    });

    return {
      properties: properties.result,
      offers: offers.result,
    };
  };

  const onSubmit = (form) => {
    form.errors = [];
    form.errorSchema = {};

    const formData = {
      ...form.formData,
      packaged_rate_id: form.formData.packaged_rate_id || null,
    };

    setSaveState(buttonStates.saving);
    setRatePlan(formData);
    delete formData.schedules; // not expected in update/create

    const toSubmit = {
      ...formData,
      bonus_inclusions: (formData.bonus_inclusions || []).map(
        ({ content, from_nights, to_nights, content_override }) => ({
          content,
          content_override,
          from_nights,
          to_nights,
        }),
      ),
      custom_vcc_payment_currency:
        formData?.custom_vcc_payment_currency && formData.custom_vcc_payment_currency !== ''
          ? formData.custom_vcc_payment_currency
          : null,
      b2b_special_payment_terms:
        formData?.b2b_special_payment_terms && formData.b2b_special_payment_terms !== ''
          ? formData.b2b_special_payment_terms
          : null,
      currency: formData?.currency && formData.currency !== '' ? formData.currency : null,
      has_ari: formData?.has_ari !== undefined && formData.has_ari !== '' ? formData.has_ari : null,
      source: formData?.source && formData.source !== '' ? formData.source : 'admin',
      deposit_percent: formData.type_of_pay === 'deposit' ? formData.deposit_percent : 0,
      schedules_list: prepareSchedulesList(formData?.schedules_list, ratePlanId),
      discounts: prepareScheduledDiscountList(formData.discounts),
    };

    // Remove empty values so validation passes
    if (toSubmit.type_of_pay === '') {
      delete toSubmit.type_of_pay;
    }
    if (toSubmit.vcc_buffer === '') {
      delete toSubmit.vcc_buffer;
    }

    const submitType = ratePlanId ? 'updateRatePlan' : 'createRatePlan';

    ReservationService[submitType](toSubmit, ratePlanId)
      .then(({ result }) => {
        history.push(`/vendors/${vendorId}/rate-plans/${result.id}`);
      })
      .catch(function (e) {
        setSaveState(buttonStates.failed);
        reportError(e);
        enqueueSnackbar(e?.errors[0] || e.message || 'Unable to save rate plan!', {
          variant: 'error',
        });
      });
  };

  const updateBonusInclusions = (bonusInclusions) => {
    const shouldMarkAsSetToMax = bonusInclusions?.find(
      (inclusions) => inclusions.to_nights === BONUS_INCLUSIONS_MAX_TO_NIGHTS && inclusions.set_to_max === undefined,
    );

    if (shouldMarkAsSetToMax) {
      shouldMarkAsSetToMax.set_to_max = true;
    }

    const toSetToMax = bonusInclusions?.find((inclusions) => inclusions.set_to_max);
    if (toSetToMax) {
      toSetToMax.to_nights = BONUS_INCLUSIONS_MAX_TO_NIGHTS;
    }

    const toRemoveToNights = bonusInclusions?.find(
      (inclusions) => !inclusions.set_to_max && inclusions.to_nights === BONUS_INCLUSIONS_MAX_TO_NIGHTS,
    );
    if (toRemoveToNights) {
      toRemoveToNights.to_nights = toRemoveToNights.from_nights;
    }
  };

  const onChange = (edit) => {
    const newRatePlan = edit.formData;
    const newUiSchema = { ...uiSchema };

    const { rate_type } = newRatePlan;
    newUiSchema.commission = rate_type === 'net' ? hiddenSchema : commissionSchema;
    newUiSchema.discount = rate_type === 'net' ? hiddenSchema : discountSchema;
    newUiSchema.markup = rate_type === 'gross' ? hiddenSchema : markupSchema;

    if (newRatePlan.bonus_inclusions) {
      updateBonusInclusions(newRatePlan.bonus_inclusions);
    }

    if (saveState !== buttonStates.default) {
      setSaveState(buttonStates.default);
    }

    if (newRatePlan.type_of_pay !== ratePlan.type_of_pay) {
      newUiSchema.deposit_percent =
        newRatePlan.type_of_pay === 'deposit'
          ? {}
          : {
              'ui:widget': 'hidden',
            };
    }

    if (newRatePlan.is_packaged_rate !== ratePlan.is_packaged_rate) {
      newUiSchema.packaged_rate_id = newRatePlan.is_packaged_rate
        ? {
            'ui:widget': 'hidden',
          }
        : {};
    }

    if (newRatePlan.bundle_and_save_discount_only !== ratePlan.bundle_and_save_discount_only) {
      newUiSchema.bundle_discount_percent = !newRatePlan.bundle_and_save_discount_only
        ? {
            'ui:widget': 'hidden',
          }
        : {
            'ui:title': 'Discount for Bundle Offer (%)',
          };
    }

    if (newRatePlan.inclusions_override !== ratePlan.inclusions_override) {
      // Only process if inclusions_override is an array
      if (Array.isArray(newRatePlan.inclusions_override)) {
        newUiSchema.promotions = newRatePlan.inclusions_override.map((promo) => ({
          ...promo,
          inclusions: (promo.inclusions ?? []).map((inclusion) => ({
            text: inclusion.text ?? '',
            category: inclusion.category ?? '',
            icon: inclusion.icon ?? '',
            isHighlighted: inclusion.isHighlighted ?? false,
          })),
        }));
      }
    }

    setUiSchema(newUiSchema);
    setRatePlan((prevRatePlan) =>
      nullsToEmptyStrings({
        ...newRatePlan,
        discounts: prevRatePlan.discounts,
        schedules_list: prevRatePlan.schedules_list,
      }),
    );
  };

  const onDelete = async () => {
    setDeleteState('deleting');
    const ratePlanIsUsed = await findUsage();

    if (ratePlanIsUsed) {
      setDeleteState('failed');
      enqueueSnackbar(
        'This rate plan is currently attached to an offer. You must detach it from any offers before you can delete.',
        {
          variant: 'error',
        },
      );
      return false;
    }

    ReservationService.deleteRatePlan(ratePlanId)
      .then(function () {
        history.push(`/vendors/${vendorId}`);
      })
      .catch((e) => {
        reportError(e);
        setDeleteState('failed');
        enqueueSnackbar(e?.errors[0] || e.message || 'Error! Unable to delete rate plan.', {
          variant: 'error',
        });
      });
  };

  const getRegionNameFromCode = (code) => {
    const region = libRegions.getRegionByCode(code, 'luxuryescapes');
    return region ? region.name : code;
  };

  const setRegions = (regions) => {
    let isAllRegionsSelected = false;
    if (regions.length === libRegions.getRegions().length) {
      isAllRegionsSelected = true;
    }

    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      regions: isAllRegionsSelected ? ['world'] : regions,
    }));
    setModalIsOpen(false);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const showModal = () => {
    setModalIsOpen(true);
  };

  const handleScheduleChange = (value, id) => {
    const schedules = { ...ratePlan.schedules_list };
    schedules[id] = value;

    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      schedules_list: schedules,
    }));
  };

  const addNewSchedule = () => {
    const schedules = { ...ratePlan.schedules_list };
    schedules[uuid()] = {
      // we don't set the id in the object, it's only used for the key
      activePeriod: {
        from: null,
        to: null,
      },
      travelPeriod: {
        from: null,
        to: null,
      },
      type: 'sell',
      parent: ratePlan.id,
    };
    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      schedules_list: schedules,
    }));
  };

  const removeSchedule = (index) => {
    const schedules = { ...ratePlan.schedules_list };
    delete schedules[index];
    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      schedules_list: schedules,
    }));
  };

  const handleScheduledDiscountChange = (value, id) => {
    const discounts = { ...ratePlan.discounts };
    discounts[id] = value;

    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      discounts,
    }));
  };

  const addNewScheduledDiscount = () => {
    const discounts = { ...ratePlan.discounts };
    discounts[uuid()] = {
      rate_plan_id: ratePlanId,
      discount_name: null,
      is_displayed: true,
      source: 'internal',
      paid_by: 'vendor', // only vendor is supported in backend currently
      discount_type: 'long_stays',
      discount_percent: 0,
      min_los: null,
      max_los: null,
      min_days_to_arrival: null, // Usable after adding promotional discounts to all channel managers
      max_days_to_arrival: null,
      luxplus_discount_percent: null,
      schedules: [
        {
          activePeriod: {
            from: null,
            to: null,
          },
          travelPeriod: {
            from: null,
            to: null,
          },
          type: 'sell',
        },
      ],
    };
    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      discounts,
    }));
  };

  const removeScheduledDiscount = (id) => {
    const discounts = { ...ratePlan.discounts };
    delete discounts[id];
    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      discounts,
    }));
  };

  const addStayPayTier = () => {
    const stayPay = {
      id: uuid(),
      rate_plan_id: ratePlanId,
      stay_nights: null,
      free_nights: null,
    };

    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      stay_pay: [...(prevRatePlan.stay_pay || []), stayPay],
    }));
  };

  const onStayPayChange = (event) => {
    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      is_stay_pay_enabled: event.target.checked,
    }));
  };

  const removeStayPayTier = (id) => {
    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      stay_pay: prevRatePlan.stay_pay.filter((stayPay) => stayPay.id !== id),
    }));
  };

  const handleStayPayTierChange = (field, value, id) => {
    const stayPay = ratePlan.stay_pay.map((tier) => {
      if (tier.id === id) {
        return {
          ...tier,
          [field]: value,
        };
      }
      return tier;
    });

    setRatePlan((prevRatePlan) => ({
      ...prevRatePlan,
      stay_pay: stayPay,
    }));
  };

  const formRatePlan = useMemo(() => {
    const discountsArray = ratePlan.discounts ? Object.values(ratePlan.discounts) : [];
    const result = { ...ratePlan };

    if (discountsArray.length === 0) {
      delete result.discounts;
    } else {
      result.discounts = discountsArray;
    }

    return result;
  }, [ratePlan]);

  return (
    <Form
      validator={validator}
      schema={schema}
      uiSchema={uiSchema}
      onChange={onChange}
      formData={formRatePlan}
      onSubmit={onSubmit}
      widgets={widgets}
      formContext={{
        rateTypeValue: formRatePlan.rate_type,
        bonusInclusions: formRatePlan.bonus_inclusions,
      }}
    >
      <div className="regions-tag-container">
        <div>
          <label className="control-label">Package Regions</label>
        </div>
        <div>
          <ul className="region-tags-list">
            {!formRatePlan.regions || formRatePlan.regions.length === libRegions.getRegions().length ? (
              <li key="world">world</li>
            ) : (
              formRatePlan.regions.map((region) => <li key={region}>{getRegionNameFromCode(region)}</li>)
            )}
          </ul>
        </div>
        <div>
          <Button variant="contained" onClick={showModal}>
            Set Regions
          </Button>
        </div>
      </div>

      <Box>
        <Typography variant="h6">Schedules</Typography>
        {ratePlan.schedules_list &&
          Object.keys(ratePlan.schedules_list).map((key) => (
            <Grid key={key} spacing={2} container alignItems="center">
              <Grid item xs={11}>
                <Schedules
                  key={key}
                  index={key}
                  value={ratePlan.schedules_list[key]}
                  onChange={(value) => handleScheduleChange(value, key)}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton color="error" onClick={() => removeSchedule(key)}>
                  <DeleteForeverIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        <Box display="flex" justifyContent="flex-end">
          <Button
            color="info"
            size="large"
            variant="contained"
            onClick={addNewSchedule}
            my={2}
            sx={{ minWidth: 200, alignSelf: 'end', width: '25%' }}
          >
            <AddIcon />
          </Button>
        </Box>
      </Box>

      <Box>
        <Typography variant="h6">Promotional Discounts</Typography>
        {ratePlan.discounts &&
          Object.keys(ratePlan.discounts).map((key) => (
            <Grid key={key} spacing={2} container alignItems="center">
              <Grid item xs={11}>
                <ScheduledDiscount
                  key={key}
                  index={key}
                  value={ratePlan.discounts[key]}
                  onChange={(value) => handleScheduledDiscountChange(value, key)}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton color="error" onClick={() => removeScheduledDiscount(key)}>
                  <DeleteForeverIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        <Box display="flex" justifyContent="flex-end">
          <Button
            color="info"
            size="large"
            variant="contained"
            onClick={addNewScheduledDiscount}
            my={2}
            sx={{ minWidth: 200, alignSelf: 'end', width: '25%' }}
          >
            <AddIcon /> Add Promotional Discount
          </Button>
        </Box>
      </Box>

      <FormControlLabel
        control={<Checkbox checked={formRatePlan.is_stay_pay_enabled} onChange={onStayPayChange} />}
        label="Enable Stay Pay"
      />
      <Paper>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Stay Pay Tiers
        </Typography>

        {ratePlan.stay_pay?.length > 0 &&
          ratePlan.stay_pay.map((tier, key) => (
            <Paper key={key} sx={{ py: 1 }}>
              <Grid spacing={2} container alignItems="center">
                <Grid item xs={1}>
                  <Typography variant="p">Tier {key + 1}</Typography>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label="Stay Nights"
                    type="number"
                    onChange={(event) => handleStayPayTierChange('stay_nights', event.target.value, tier.id)}
                    value={tier.stay_nights}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label="Free Nights"
                    type="number"
                    onChange={(event) => handleStayPayTierChange('free_nights', event.target.value, tier.id)}
                    value={tier.free_nights}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton color="error" onClick={() => removeStayPayTier(tier.id)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}

        {ratePlan.stay_pay?.length === 0 && <Typography variant="p">No Stay Pay Tiers</Typography>}
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <Button
            color="info"
            size="large"
            variant="contained"
            onClick={addStayPayTier}
            my={2}
            sx={{ minWidth: 200, alignSelf: 'end', width: '50%' }}
          >
            <AddIcon /> Add Stay Pay Tier
          </Button>
        </Box>
      </Paper>

      <div className="button-container">
        <Button type="submit" variant="contained" className={saveState}>
          {buttonMessages[saveState]}
        </Button>
        <Box ml={2}>{ratePlanId ? <DeleteButton onClick={onDelete} buttonText={deleteState} /> : null}</Box>
      </div>

      <SetRegionModal
        open={modalIsOpen}
        onRequestClose={closeModal}
        onSet={setRegions}
        regions={formRatePlan.regions ?? ['world']}
        name="Package Regions"
        label="Tag all regions that you want this package to be visible in"
      />
    </Form>
  );
}

export default withSnackbar(RatePlanForm);
